/**
 * Where somebody came from, captured once and kept.
 *
 * Paid traffic arrives on a url carrying utm parameters and then, one tap
 * later, is on a url that carries none. So the first landing is the only moment
 * this is knowable: read it then, keep it, and attach it to everything
 * afterwards. Without this, cost per click is the only number an ad platform
 * can be held to, and cost per click is not a number worth bidding on.
 *
 * First touch wins. A person who arrives from an ad, leaves, and comes back
 * through a friend's share link is still someone the ad found; overwriting on
 * the second visit would quietly credit the loop for work the ad did.
 */
import { DEFAULT_DOOR, isDoor } from '../data/doors';
import { starterFrom } from './starter';

const KEY = 'kindling.campaign';

export interface Campaign {
  source?: string;
  medium?: string;
  campaign?: string;
  content?: string;
  term?: string;
  /** The first url's path, which says which door they came through. */
  landing?: string;
  /**
   * The front door they arrived through (17 §6.2), resolved once on the first
   * landing and then fixed forever. It is a fact about somebody's *first*
   * arrival: re-resolving it when they come back through a different ad would
   * quietly move them between cohorts and corrupt every funnel it appears in.
   */
  door?: string;
  /**
   * The first activity they were sent to (`?start=imagine`, lib/starter.ts,
   * well/product/33), fixed on the first landing for the same reason the door
   * is. Absent is the default warm-up.
   */
  start?: string;
  at?: number;
}

/** Only the string-valued tags; `landing` and `at` are set by us, not read. */
type Tag = 'source' | 'medium' | 'campaign' | 'content' | 'term';

const PARAMS: [Tag, string][] = [
  ['source', 'utm_source'],
  ['medium', 'utm_medium'],
  ['campaign', 'utm_campaign'],
  ['content', 'utm_content'],
  ['term', 'utm_term'],
];

function read(): Campaign | null {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as Campaign) : null;
  } catch {
    return null;
  }
}

/**
 * Which front door this url is. Three ways in, in order of how explicit they
 * are: `?d=term` on any url, the doorway page's own path `/d/term`, and
 * `utm_content` when whoever built the ad named the door there and nothing
 * else said otherwise.
 *
 * An unknown slug is the default door, not a blank screen: links outlive the
 * campaigns that minted them, and a dead slug must still open the app. The
 * miss is kept as `content` through the normal utm path, so it is visible in
 * the data rather than silently swallowed.
 */
function doorFrom(url: URL): string {
  const explicit = url.searchParams.get('d');
  if (isDoor(explicit)) return explicit as string;

  const path = url.pathname.match(/^\/d\/([^/]+)/);
  if (path && isDoor(path[1])) return path[1];

  const content = url.searchParams.get('utm_content');
  if (isDoor(content)) return content as string;

  return DEFAULT_DOOR;
}

/**
 * Reads the current url and keeps what it finds, unless something is already
 * kept. Returns whatever is known now, which may be nothing at all — most
 * people arrive with no parameters, and that is itself worth recording as
 * "direct" rather than as a gap.
 */
export function captureCampaign(href = typeof window !== 'undefined' ? window.location.href : ''): Campaign | null {
  if (!href) return read();

  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return read();
  }

  // `?fresh` is the "pretend I have never been here" flag, and it already wipes
  // the profile (App.tsx). It has to wipe this too, or first touch pins the
  // tester to whatever door they happened to arrive through first and no door
  // can ever be looked at twice on one machine.
  const wipe = url.searchParams.has('fresh') || url.searchParams.has('newuser');
  const existing = wipe ? null : read();
  if (existing) return existing;

  const found: Campaign = {};
  for (const [key, param] of PARAMS) {
    const v = url.searchParams.get(param);
    if (v) found[key] = v.slice(0, 120);
  }
  // A click id with no utm tags still means paid: Meta, Google and TikTok all
  // append one whether or not the tags were set up properly.
  if (!found.source) {
    if (url.searchParams.get('fbclid')) found.source = 'facebook';
    else if (url.searchParams.get('gclid')) found.source = 'google';
    else if (url.searchParams.get('ttclid')) found.source = 'tiktok';
  }

  const start = starterFrom(url);
  const campaign: Campaign = { ...found, landing: url.pathname, door: doorFrom(url), ...(start ? { start } : {}), at: Date.now() };
  if (!campaign.source) campaign.source = 'direct';

  try {
    localStorage.setItem(KEY, JSON.stringify(campaign));
  } catch {
    // A browser that refuses storage still gets the value for this session.
  }
  return campaign;
}

export function loadCampaign(): Campaign | null {
  return read();
}
